import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { updateBrandColor } from "./brands.thunk";

export interface BrandValue {
  brandId: number | string;
  name: string;
  displayName: string;
  logo: string;
}

export interface BrandSetMember {
  id: number;
  weight: number;
  logo?: string | null;
  name?: string;
}

export interface Brand {
  id: number | string;
  name: string;
  logo: string;
  displayName: string;
  type: string;
  position: number | null;
  color: string | null;
  marketIds: number[];
  reputationMarketIds?: number[];
  brandValues?: BrandValue[];
  // Brand set specific fields
  description?: string;
  ulid?: string | null;
  weighted?: boolean | null;
  members?: BrandSetMember[];
}

const brandsAdapter = createEntityAdapter<Brand, number | string>({
  selectId: (brand: Brand): number | string => brand.id,
  sortComparer: (a: Brand, b: Brand) =>
    a?.displayName && b.displayName ? a.displayName.localeCompare(b.displayName) : a.name.localeCompare(b.name),
});

const initialState = brandsAdapter.getInitialState();

export const brandsSlice = createSlice({
  name: "brands",
  initialState,
  reducers: {
    setBrands: (state, action: PayloadAction<any[]>) => {
      brandsAdapter.setAll(state, action.payload);
    },
    addBrand: (state, action: PayloadAction<Brand>) => {
      brandsAdapter.addOne(state, action.payload);
    },
    updateBrand: (
      state,
      action: PayloadAction<{ id: number | string; changes: Partial<Brand> }>,
    ) => {
      brandsAdapter.updateOne(state, action.payload);
    },
    removeBrand: (state, action: PayloadAction<number | string>) => {
      brandsAdapter.removeOne(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateBrandColor.fulfilled, (state, action) => {
      if (action.payload !== null) {
        brandsAdapter.updateOne(state, {
          id: action.payload.brandId,
          changes: {
            color: action.payload.color,
          },
        });
      }
    });
  },
});

export type BrandsAdapterType = EntityState<Brand, number | string>;

export const { setBrands, addBrand, updateBrand, removeBrand } = brandsSlice.actions;

export default brandsSlice.reducer;
